import { connect } from 'pwa-helpers';
import { LitElement, html, css } from "lit";
import { customElement, property, state } from 'lit/decorators.js';
import store from "../../store/store";
import { StoreState } from "../../store/store.interface";
import textStyles from '../../common/styles/text-styles';
import variables from '../../common/styles/variables';
import { CustomerProfile } from '../customer/customer.interface';
import { fetchUserProfile } from '../user-profile/user-profile.thunks';
import { selectRequest, selectTotal } from '../search/store/search.selector';
import { getCart } from '../cart/shop-cart/store/shop-cart.selectors';
import { isCustomerLoadSuccess, selectCustomer } from '../customer/customer';
import { selectUser } from '../user-profile/user-profile.selectors';
import { createMessage } from '../../common/utils/util';
import { getShopCart } from '../cart/shop-cart/thunks/get-shop-cart.thunk';

/**
 * An element to show a message that includes contextual information.
 *
 * @csspart number-of-items
 * @csspart cart-size
 * @csspart first-name
 * @csspart last-name
 * @csspart customer-first-name
 * @csspart customer-last-name
 * @csspart customer-logged-in
 * @csspart pagination-first
 * @csspart pagination-last
 * @csspart pagination-total
 * @csspart search-term
 */
@customElement('mhecomm-message')
export class Message extends connect(store)(LitElement) {
  static styles = [variables, textStyles, css`
    :host {
      display: inline;
    }
  `];

  /**
   * The message to display. The following placeholders will be replaced: %number-of-items% %cart-size% %customer-logged-in% %customer-first-name% %customer-last-name% %first-name% %last-name%
   */
  @property()
  message = '';

  @property()
  store?: string

  /**
   * When user pass this property, depending on its value arialive property will get set to the compiled message
   */
  @property()
  arialive?: string;

  @state()
  private compiledMessage?: HTMLElement;

  private customer?: CustomerProfile;
  private isCustomerLoggedIn = false;
  private itemsInCart?: string;
  private firstName?: string;
  private lastName?: string;
  private cartSize?: number;
  private start?: number;
  private end?: number;
  private total?: number;
  private term?: string;

  connectedCallback() {
    super.connectedCallback();
    this.calculateMessage();

    store.dispatch(fetchUserProfile());
    store.dispatch(getShopCart({ store: this.store }));
  }

  render() {
    return html`
      ${this.compiledMessage ? this.compiledMessage : ''}
    `;
  }

  update(changedProperties: any) {
    super.update(changedProperties);

    if (changedProperties.has('message')) {
      this.calculateMessage();
    }
  }

  stateChanged(state: StoreState) {
    const cart = getCart(state);
    const request = selectRequest(state) ?? undefined;
    const maxEnd = (request?.start ?? 0) + (request?.pageLength ? request?.pageLength - 1 : 0);
    this.customer = selectCustomer(state);
    this.isCustomerLoggedIn = isCustomerLoadSuccess(state);
    this.firstName = selectUser(state)?.givenName;
    this.lastName = selectUser(state)?.familyName;
    this.total = selectTotal(state) || 0;
    this.cartSize = cart === undefined ? 0 : cart.productCount;
    this.itemsInCart = this.cartSize === 0 ? '0 items' : this.cartSize === 1 ? this.cartSize + ' item' : this.cartSize + ' items';
    this.term = request?.searchTerms ?? '';
    this.start = request?.start ?? 0;
    this.end = maxEnd > this.total ? this.total : maxEnd;
    this.calculateMessage();
  }

  calculateMessage() {
    this.compiledMessage = createMessage(this.message, {
      'number-of-items': this.itemsInCart,
      'cart-size': this.cartSize?.toLocaleString(),
      'first-name': this.firstName,
      'last-name': this.lastName,
      'customer-first-name': this.customer?.firstname,
      'customer-last-name': this.customer?.lastname,
      'customer-logged-in': this.isCustomerLoggedIn ? 'Logged in' : 'Logged out',
      'pagination-first': this.start?.toLocaleString(),
      'pagination-last': this.end?.toLocaleString(),
      'pagination-total': this.total?.toLocaleString(),
      'search-term': this.term
    }, this.arialive);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-message': Message;
  }
}
